import { Chip } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { computeDistanceInKm } from '@kheops/utils';
import { GeoLoc } from '../common/models/user.models';
import { currentContextAtom } from '../state';
import { CompanyCategoryEnum, CompanyCategoryIconMap } from './state';

export interface CompanyCategoryTagProps {
  targetGeoLoc: GeoLoc;
  isWholesaler?: boolean;
  variant?: 'chip' | 'text';
}

export default function CompanyCategoryTag({ targetGeoLoc, isWholesaler, variant = 'chip' }: CompanyCategoryTagProps): React.JSX.Element {
  const { t } = useTranslation(['social-proof']);
  const { companyGeoloc } = useAtomValue(currentContextAtom);
  
  const distance = useMemo((): number | undefined => {
    if (!companyGeoloc || !targetGeoLoc) {
      return;
    }
    return computeDistanceInKm(
      targetGeoLoc.lat, targetGeoLoc.lng,
      companyGeoloc.lat, companyGeoloc.lng,
    );
  }, [targetGeoLoc, companyGeoloc]);
  const distanceLabel = useMemo((): string => {
    if (distance === undefined) {
      return '';
    }
    
    return ` (${distance.toFixed(0)} km)`;
  }, [distance]);

  const categories = useMemo((): CompanyCategoryEnum[] => {
    const result: CompanyCategoryEnum[] = [];    
    if (isWholesaler) {
      result.push(CompanyCategoryEnum.WHOLESALER);
    } else {
      result.push(CompanyCategoryEnum.NON_LOCAL_PRODUCER);
    }

    if (distance !== undefined && distance < 100) {
      result.push(CompanyCategoryEnum.LOCAL_PRODUCER);
    }

    return result;
  }, [distance]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {
        variant === 'chip'
          ? categories.map((category) => (
            <Chip
              key={category}
              variant="outlinedTag"
              label={t(`social-proof:${category.toLowerCase()}.title`) + (category === 'LOCAL_PRODUCER' ? distanceLabel : '')}
              icon={<img src={CompanyCategoryIconMap[category]} loading="lazy" alt="" />}
            />
          ))
          : categories.map((category) => t(`social-proof:${category.toLowerCase()}.title`)).join(' - ')
      }
    </>
  );
}
